import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Button,
	Chip,
	IconButton,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Link, useNavigate, useParams } from 'react-router-dom';
import LocalDataGrid from '@/common/components/dataGrid/LocalDataGrid';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { GridColDef } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import {
	DISPLAY_DATE_FORMAT,
	DISPLAY_DATE_TIME_FORMAT,
} from '@/utils/dateHelper';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import { useDeleteFaceSheetMutation } from '@/api/forms-management/faceSheet';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import {
	FormManagementStatus,
	FormManagementType,
	FormManagementTypeEnum,
	PermissionEnum,
	PreAdmissionTab,
} from '@/api/enum/common.enum';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import { ReactElement } from 'react';
import FormManagementChip from '@/common/components/FormManagementChip';
import {
	useDeleteUploadFormMutation,
	useGetExportCustomerPDFMutation,
	useGetFaceSheetGenerateReportMutation,
	useGetFamilyGenerateReportMutation,
} from '@/api/common';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { useDeleteFamilyMemberFormMutation } from '@/api/family-management/familyApi';
import { useGetUser } from '@/hooks/useGetUser';

interface Props {
	expanded: boolean;
	type: string;
	buttonElement: ReactElement;
	tableList: any;
	title: string;
	setExpanded: (value: boolean) => void;
}
export default function FormListContainer(props: Props) {
	const { expanded, tableList, type, buttonElement, setExpanded, title } =
		props;
	const { t } = useTranslation();
	const { familyId } = useParams();
	const [getExportCustomerPDF] = useGetExportCustomerPDFMutation();
	const [getFamilyGenerateReport] = useGetFamilyGenerateReportMutation();
	const [deleteFamilyMemberForm] = useDeleteFamilyMemberFormMutation();
	const [deleteUploadForm] = useDeleteUploadFormMutation();
	const { userInfo } = useGetUser();

	const showModal = useModal();

	const columns: GridColDef[] = [
		{
			field: 'versionNo',
			headerName: t('preAdmission.version'),
			flex: 25,
		},
		{
			field: 'formStatusDesc',
			headerName: t('preAdmission.formStatus'),
			flex: 25,
			renderCell: (params: any) => (
				<FormManagementChip
					status={params.row.formStatus}
					label={params.row.formStatusDesc}
				/>
			),
		},
		{
			field: 'fileName',
			headerName: t('preAdmission.fileName'),
			flex: 25,
		},
		{
			field: 'description',
			headerName: t('preAdmission.description'),
			flex: 25,
		},
		{
			field: 'createdAt',
			headerName: t('preAdmission.uploadedDate'),
			flex: 25,
			renderCell: (params: any) =>
				!params.row.createdAt
					? ''
					: dayjs(params.row.createdAt)
							.locale('en')
							.format(DISPLAY_DATE_FORMAT),
		},
		{
			field: 'updatedAt',
			headerName: t('preAdmission.modifiedDateTime'),
			flex: 25,
			renderCell: (params: any) =>
				!params.row.updatedAt
					? ''
					: dayjs(params.row.updatedAt)
							.locale('en')
							.format(DISPLAY_DATE_TIME_FORMAT),
		},
		{
			field: 'actionedBy',
			headerName: t('preAdmission.actionedBy'),
			flex: 25,
		},
		{
			field: 'edit',
			headerName: '',
			width: 1,
			renderCell: (params: any) =>
				((params.row.formStatus == FormManagementStatus.submitted &&
					userInfo?.permissionList?.includes(
						PermissionEnum.ViewSubmittedOrUploadedForm
					)) ||
					(params.row.formStatus == FormManagementStatus.draft &&
						userInfo?.permissionList?.includes(
							PermissionEnum.EditDraftForm
						))) &&
				(params.row.formStatus == FormManagementStatus.submitted ||
					params.row.formStatus == FormManagementStatus.draft) && (
					<Link
						to={`/family-management/family-management-list/detail/${familyId}/forms-management/detail/${params.row.id}`}
					>
						<IconButton color='secondary'>
							{params.row.formStatus == FormManagementStatus.submitted ? (
								<VisibilityOutlinedIcon />
							) : params.row.formStatus == FormManagementStatus.draft ? (
								<CreateOutlinedIcon />
							) : (
								''
							)}
						</IconButton>
					</Link>
				),
		},
		{
			field: 'downLoad',
			headerName: '',
			width: 1,
			renderCell: (params: any) =>
				userInfo?.permissionList?.includes(PermissionEnum.DownloadForm) && (
					<IconButton
						color='secondary'
						onClick={async () => {
							console.log(params.row);
							if (
								params.row.formStatus == FormManagementStatus.submitted ||
								params.row.formStatus == FormManagementStatus.draft
							) {
								let response: any = {};
								if (
									params.row.formType ==
									FormManagementTypeEnum[FormManagementType.familyMember]
								) {
									response = await getFamilyGenerateReport(params.row.id);
								}

								if ('data' in response) {
									getExportCustomerPDF(response?.data?.data.url);
								}
							} else {
								getExportCustomerPDF(params.row.filePath);
							}
						}}
					>
						<FileDownloadOutlinedIcon />
					</IconButton>
				),
		},
		{
			field: 'delete',
			headerName: '',
			width: 1,
			renderCell: (params: any) =>
				// Special logic for the Family Member Form List:
				// The server always returns exactly ONE submitted "Family member / Carer
				// Member Application Form" and that submitted form is NOT deletable.
				// Users may additionally upload their own "Uploaded Form"s, and only those
				// uploaded forms can be deleted — so the delete icon is shown for the
				// uploaded status only (gated by DeleteSubmittedOrUploadedForm).
				params.row.formStatus == FormManagementStatus.uploaded &&
				userInfo?.permissionList?.includes(
					PermissionEnum.DeleteSubmittedOrUploadedForm
				) && (
					<IconButton
						color='secondary'
						onClick={() => {
							handleDelete(params.row.id, params.row);
						}}
					>
						<DeleteOutlinedIcon />
					</IconButton>
				),
		},
	];
	const handleDelete = async (id: string, row: any) => {
		// Only uploaded forms are deletable here (the single submitted Family Member
		// Application Form is not), so deletion always goes through the upload-form API.
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={false}
				title={t('preAdmission.deleteConfirmation')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>
						{t('preAdmission.isDeleteForm', {
							formName: t('family.familyMemberApplicationFormForm'),
						})}
					</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.confirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const response = await deleteUploadForm(id);
					if ('data' in response) {
						enqueueSnackbar(t('snackbar.deleteSuccess'), {
							variant: 'warning',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});
					}
				}}
			/>
		));
	};

	return (
		<>
			<Accordion
				expanded={expanded}
				onChange={() => setExpanded(!expanded)}
				sx={{
					border: '1px solid #D7D7D7',
					borderRadius: '16px!important',
					'&::before': {
						backgroundColor: 'transparent !important',
					},
					marginBottom: '24px',
					padding: '8px',
				}}
			>
				<AccordionSummary expandIcon={<ExpandMoreIcon />}>
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'space-between',
							width: '100%',
						}}
					>
						<Typography
							sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}
						>
							{title}
							{tableList?.data.length > 0 && (
								<span
									style={{
										marginLeft: '8px',
										display: 'inline-block',
										backgroundColor: '#646D11',
										color: 'white',
										borderRadius: '50%',
										minWidth: '16px',
										height: '16px',
										maxWidth: '34px',
										textAlign: 'center',
										fontSize: '12px',
									}}
								>
									{tableList?.data.length || 0}
								</span>
							)}
						</Typography>

						<Box marginRight={2}>{buttonElement}</Box>
					</Box>
				</AccordionSummary>
				<AccordionDetails>
					<LocalDataGrid
						rows={tableList?.data ?? []}
						columns={columns}
						getRowId={(row) => row.id}
						pagination={false}
					/>
				</AccordionDetails>
			</Accordion>
		</>
	);
}
